import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  X,
  Loader2,
  Trophy,
  Clock,
  Users,
  CheckCircle2,
  Plus,
  Flame,
  Flag,
  Timer,
  Image as ImageIcon
} from 'lucide-react';
import { Button, Card, Modal, ConfirmModal, Textarea, Input, Select, EmptyState } from '../../components/ui';
import { AppLayout } from '../../components/AppLayout';
import { useAuth } from '../../context';
import {
  challengesService,
  ChallengeDto,
  ChallengeParticipationDto,
  CreateCustomChallengeRequest,
  CompleteChallengeRequest
} from '../../services';
import './Challenges.css';

// Tab types
type ChallengesTab = 'library' | 'active' | 'completed';

// Discipline options
const disciplineOptions = [
  { value: 'music', label: 'Music' },
  { value: 'visual-art', label: 'Visual Art' },
  { value: 'writing', label: 'Writing' },
  { value: 'film', label: 'Film / Video' },
  { value: 'photography', label: 'Photography' },
  { value: 'dance', label: 'Dance' },
  { value: 'theater', label: 'Theater' },
  { value: 'design', label: 'Design' },
  { value: 'software', label: 'Software' },
  { value: 'mixed-media', label: 'Mixed Media' },
  { value: 'other', label: 'Other' },
];
const disciplineFilterOptions = [
  { value: '', label: 'All Disciplines' },
  ...disciplineOptions,
];

// Difficulty metadata
const difficultyMeta: Record<string, { label: string; color: string }> = {
  Easy: { label: 'Easy', color: 'success' },
  Medium: { label: 'Medium', color: 'warning' },
  Hard: { label: 'Hard', color: 'error' },
};

// Difficulty options for select
const difficultyOptions = [
  { value: '', label: 'All Difficulties' },
  { value: 'Easy', label: 'Easy' },
  { value: 'Medium', label: 'Medium' },
  { value: 'Hard', label: 'Hard' },
];

// Duration type options
const durationTypeOptions = [
  { value: 'Minutes', label: 'Minutes' },
  { value: 'Hours', label: 'Hours' },
  { value: 'Days', label: 'Days' },
  { value: 'Weeks', label: 'Weeks' },
];

// Format duration display
const formatDuration = (duration: number, durationType: string): string => {
  const unit = durationType.toLowerCase();
  if (duration === 1) return `1 ${unit.replace(/s$/, '')}`;
  return `${duration} ${unit.toLowerCase()}`;
};

// Format relative date
const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return date.toLocaleDateString();
};

export const Challenges: React.FC = () => {
  useAuth();

  // Tab state
  const [activeTab, setActiveTab] = useState<ChallengesTab>('library');

  // Library state
  const [challenges, setChallenges] = useState<ChallengeDto[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [disciplineFilter, setDisciplineFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const [isLoadingLibrary, setIsLoadingLibrary] = useState(true);
  const [libraryError, setLibraryError] = useState<string | null>(null);

  // Active challenges state
  const [activeChallenges, setActiveChallenges] = useState<ChallengeParticipationDto[]>([]);
  const [isLoadingActive, setIsLoadingActive] = useState(false);
  const [activeError, setActiveError] = useState<string | null>(null);

  // Completed challenges state
  const [completedChallenges, setCompletedChallenges] = useState<ChallengeParticipationDto[]>([]);
  const [isLoadingCompleted, setIsLoadingCompleted] = useState(false);
  const [completedError, setCompletedError] = useState<string | null>(null);

  // Detail view state
  const [selectedChallenge, setSelectedChallenge] = useState<ChallengeDto | null>(null);
  const [challengeResults, setChallengeResults] = useState<ChallengeParticipationDto[]>([]);
  const [isLoadingResults, setIsLoadingResults] = useState(false);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showAbandonConfirm, setShowAbandonConfirm] = useState(false);
  const [completingChallengeId, setCompletingChallengeId] = useState<string | null>(null);
  const [abandoningChallengeId, setAbandoningChallengeId] = useState<string | null>(null);

  // Create challenge form state
  const [createForm, setCreateForm] = useState<CreateCustomChallengeRequest>({
    title: '',
    description: '',
    disciplines: [] as string[],
    duration: 30,
    durationType: 'Minutes',
    difficulty: 'Easy',
  });
  const [isCreating, setIsCreating] = useState(false);

  // Complete challenge form state
  const [completeForm, setCompleteForm] = useState<CompleteChallengeRequest>({
    resultText: '',
    resultMediaUrls: [],
    durationMinutes: undefined,
  });
  const [mediaUrlInput, setMediaUrlInput] = useState('');
  const [isCompleting, setIsCompleting] = useState(false);

  // Join loading state
  const [joiningId, setJoiningId] = useState<string | null>(null);

  // Abandon loading state
  const [isAbandoning, setIsAbandoning] = useState(false);

  // Fetch library challenges
  const fetchLibrary = useCallback(async () => {
    try {
      setIsLoadingLibrary(true);
      setLibraryError(null);
      const data = await challengesService.getChallengeLibrary({
        discipline: disciplineFilter || undefined,
        difficulty: difficultyFilter || undefined,
        search: searchQuery || undefined,
      });
      setChallenges(data);
    } catch (err) {
      console.error('Failed to fetch challenges:', err);
      setLibraryError('Failed to load challenges. Please try again.');
    } finally {
      setIsLoadingLibrary(false);
    }
  }, [disciplineFilter, difficultyFilter, searchQuery]);

  // Fetch active challenges
  const fetchActive = useCallback(async () => {
    try {
      setIsLoadingActive(true);
      setActiveError(null);
      const data = await challengesService.getActiveChallenges();
      setActiveChallenges(data);
    } catch (err) {
      console.error('Failed to fetch active challenges:', err);
      setActiveError('Failed to load active challenges. Please try again.');
    } finally {
      setIsLoadingActive(false);
    }
  }, []);

  // Fetch completed challenges
  const fetchCompleted = useCallback(async () => {
    try {
      setIsLoadingCompleted(true);
      setCompletedError(null);
      const data = await challengesService.getCompletedChallenges();
      setCompletedChallenges(data);
    } catch (err) {
      console.error('Failed to fetch completed challenges:', err);
      setCompletedError('Failed to load completed challenges. Please try again.');
    } finally {
      setIsLoadingCompleted(false);
    }
  }, []);

  // Fetch challenge results for detail view
  const fetchResults = useCallback(async (challengeId: string) => {
    try {
      setIsLoadingResults(true);
      const data = await challengesService.getChallengeResults(challengeId);
      setChallengeResults(data);
    } catch (err) {
      console.error('Failed to fetch results:', err);
      setChallengeResults([]);
    } finally {
      setIsLoadingResults(false);
    }
  }, []);

  // Load data based on active tab
  useEffect(() => {
    if (activeTab === 'library') {
      fetchLibrary();
    } else if (activeTab === 'active') {
      fetchActive();
    } else if (activeTab === 'completed') {
      fetchCompleted();
    }
  }, [activeTab, fetchLibrary, fetchActive, fetchCompleted]);

  // Open challenge detail
  const handleViewChallenge = async (challenge: ChallengeDto) => {
    setSelectedChallenge(challenge);
    fetchResults(challenge.id);
  };

  // Join a challenge
  const handleJoinChallenge = async (challengeId: string) => {
    try {
      setJoiningId(challengeId);
      await challengesService.joinChallenge(challengeId);
      // Refresh library to update participant counts
      fetchLibrary();
      // If active tab data exists, refresh it
      if (activeChallenges.length > 0) {
        fetchActive();
      }
    } catch (err) {
      console.error('Failed to join challenge:', err);
      setLibraryError('Failed to join challenge. You may already be participating.');
    } finally {
      setJoiningId(null);
    }
  };

  // Open complete modal
  const handleOpenComplete = (participationId: string) => {
    setCompletingChallengeId(participationId);
    setCompleteForm({ resultText: '', resultMediaUrls: [], durationMinutes: undefined });
    setMediaUrlInput('');
    setShowCompleteModal(true);
  };

  // Submit challenge completion
  const handleCompleteChallenge = async () => {
    if (!completingChallengeId) return;

    try {
      setIsCompleting(true);
      await challengesService.completeChallenge(completingChallengeId, {
        resultText: completeForm.resultText || undefined,
        resultMediaUrls: completeForm.resultMediaUrls && completeForm.resultMediaUrls.length > 0
          ? completeForm.resultMediaUrls
          : undefined,
        durationMinutes: completeForm.durationMinutes || undefined,
      });
      setShowCompleteModal(false);
      setCompletingChallengeId(null);
      fetchActive();
      fetchCompleted();
    } catch (err) {
      console.error('Failed to complete challenge:', err);
    } finally {
      setIsCompleting(false);
    }
  };

  // Add media URL
  const handleAddMediaUrl = () => {
    if (mediaUrlInput.trim()) {
      setCompleteForm(prev => ({
        ...prev,
        resultMediaUrls: [...(prev.resultMediaUrls || []), mediaUrlInput.trim()],
      }));
      setMediaUrlInput('');
    }
  };

  // Remove media URL
  const handleRemoveMediaUrl = (index: number) => {
    setCompleteForm(prev => ({
      ...prev,
      resultMediaUrls: (prev.resultMediaUrls || []).filter((_, i) => i !== index),
    }));
  };

  // Abandon challenge
  const handleOpenAbandon = (participationId: string) => {
    setAbandoningChallengeId(participationId);
    setShowAbandonConfirm(true);
  };

  const handleAbandonChallenge = async () => {
    if (!abandoningChallengeId) return;

    try {
      setIsAbandoning(true);
      await challengesService.abandonChallenge(abandoningChallengeId);
      setShowAbandonConfirm(false);
      setAbandoningChallengeId(null);
      fetchActive();
    } catch (err) {
      console.error('Failed to abandon challenge:', err);
    } finally {
      setIsAbandoning(false);
    }
  };

  // Create custom challenge
  const handleCreateChallenge = async () => {
    if (!createForm.title.trim() || !createForm.description.trim()) return;

    try {
      setIsCreating(true);
      await challengesService.createCustomChallenge(createForm);
      setShowCreateModal(false);
      setCreateForm({
        title: '',
        description: '',
        disciplines: [],
        duration: 30,
        durationType: 'Minutes',
        difficulty: 'Easy',
      });
      fetchLibrary();
    } catch (err) {
      console.error('Failed to create challenge:', err);
    } finally {
      setIsCreating(false);
    }
  };

  // Render library tab
  const renderLibrary = () => (
    <div className="challenges__library">
      {/* Filters */}
      <div className="challenges__filters">
        <div className="challenges__search">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search challenges..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="challenges__search-clear">
              <X size={16} />
            </button>
          )}
        </div>

        <div className="challenges__filter-row">
          <Select
            options={disciplineFilterOptions}
            value={disciplineFilter}
            onChange={(e) => setDisciplineFilter(e.target.value)}
            placeholder="All Disciplines"
          />
          <Select
            options={difficultyOptions}
            value={difficultyFilter}
            onChange={(e) => setDifficultyFilter(e.target.value)}
            placeholder="All Difficulties"
          />
        </div>
      </div>

      {/* Challenge list */}
      <div className="challenges__grid">
        {isLoadingLibrary ? (
          <div className="challenges__loading">
            <Loader2 size={32} className="challenges__loading-spinner" />
            <p>Loading challenges...</p>
          </div>
        ) : libraryError ? (
          <div className="challenges__error">
            <p>{libraryError}</p>
            <Button onClick={fetchLibrary}>Try Again</Button>
          </div>
        ) : challenges.length > 0 ? (
          challenges.map((challenge, index) => (
            <Card
              key={challenge.id}
              variant="default"
              padding="md"
              className="challenges__card"
              onClick={() => handleViewChallenge(challenge)}
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              <div className="challenges__card-header">
                <div className="challenges__discipline-badges">
                  {challenge.disciplines?.map((d: string) => (
                    <span key={d} className="challenges__discipline-badge">
                      {disciplineOptions.find(o => o.value === d)?.label || d}
                    </span>
                  ))}
                </div>
                <span className={`challenges__difficulty challenges__difficulty--${difficultyMeta[challenge.difficulty]?.color || 'warning'}`}>
                  {difficultyMeta[challenge.difficulty]?.label || challenge.difficulty}
                </span>
              </div>

              <h3 className="challenges__card-title">{challenge.title}</h3>
              <p className="challenges__card-description">{challenge.description}</p>

              <div className="challenges__card-meta">
                <span className="challenges__card-meta-item">
                  <Timer size={14} />
                  {formatDuration(challenge.duration, challenge.durationType)}
                </span>
                <span className="challenges__card-meta-item">
                  <Users size={14} />
                  {challenge.participantCount}
                </span>
                <span className="challenges__card-meta-item">
                  <CheckCircle2 size={14} />
                  {challenge.completionCount}
                </span>
              </div>

              <div className="challenges__card-actions" onClick={(e) => e.stopPropagation()}>
                <Button
                  size="sm"
                  onClick={() => handleJoinChallenge(challenge.id)}
                  loading={joiningId === challenge.id}
                  disabled={joiningId === challenge.id}
                >
                  <Flag size={14} />
                  Join Challenge
                </Button>
              </div>
            </Card>
          ))
        ) : (
          <EmptyState
            title="No challenges found"
            message="Try adjusting your filters or create a custom challenge."
            icon={<Trophy size={48} />}
            action={{
              label: 'Create Challenge',
              onClick: () => setShowCreateModal(true),
            }}
          />
        )}
      </div>
    </div>
  );

  // Render active tab
  const renderActive = () => (
    <div className="challenges__active">
      {isLoadingActive ? (
        <div className="challenges__loading">
          <Loader2 size={32} className="challenges__loading-spinner" />
          <p>Loading active challenges...</p>
        </div>
      ) : activeError ? (
        <div className="challenges__error">
          <p>{activeError}</p>
          <Button onClick={fetchActive}>Try Again</Button>
        </div>
      ) : activeChallenges.length > 0 ? (
        <div className="challenges__participation-list">
          {activeChallenges.map((participation, index) => (
            <Card
              key={participation.id}
              variant="default"
              padding="md"
              className="challenges__participation-card"
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              <div className="challenges__participation-header">
                <div className="challenges__participation-status challenges__participation-status--active">
                  <Flame size={14} />
                  Active
                </div>
                <span className="challenges__participation-date">
                  <Clock size={12} />
                  Joined {formatDate(participation.joinedAt)}
                </span>
              </div>

              <h3 className="challenges__participation-title">{participation.challengeTitle}</h3>

              <div className="challenges__participation-actions">
                <Button
                  size="sm"
                  onClick={() => handleOpenComplete(participation.challengeId)}
                >
                  <CheckCircle2 size={14} />
                  Complete
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleOpenAbandon(participation.challengeId)}
                >
                  <X size={14} />
                  Abandon
                </Button>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <EmptyState
          title="No active challenges"
          message="Browse the library and join a challenge to push your creative boundaries."
          icon={<Flame size={48} />}
          action={{
            label: 'Browse Library',
            onClick: () => setActiveTab('library'),
          }}
        />
      )}
    </div>
  );

  // Render completed tab
  const renderCompleted = () => (
    <div className="challenges__completed">
      {isLoadingCompleted ? (
        <div className="challenges__loading">
          <Loader2 size={32} className="challenges__loading-spinner" />
          <p>Loading completed challenges...</p>
        </div>
      ) : completedError ? (
        <div className="challenges__error">
          <p>{completedError}</p>
          <Button onClick={fetchCompleted}>Try Again</Button>
        </div>
      ) : completedChallenges.length > 0 ? (
        <div className="challenges__participation-list">
          {completedChallenges.map((participation, index) => (
            <Card
              key={participation.id}
              variant="default"
              padding="md"
              className="challenges__participation-card"
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              <div className="challenges__participation-header">
                <div className="challenges__participation-status challenges__participation-status--completed">
                  <Trophy size={14} />
                  Completed
                </div>
                <span className="challenges__participation-date">
                  <Clock size={12} />
                  {participation.completedAt ? formatDate(participation.completedAt) : formatDate(participation.createdAt)}
                </span>
              </div>

              <h3 className="challenges__participation-title">{participation.challengeTitle}</h3>

              {participation.resultText && (
                <p className="challenges__participation-result">{participation.resultText}</p>
              )}

              {participation.resultMediaUrls && participation.resultMediaUrls.length > 0 && (
                <div className="challenges__participation-media">
                  {participation.resultMediaUrls.map((url, i) => (
                    <a
                      key={i}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="challenges__media-link"
                    >
                      <ImageIcon size={14} />
                      Media {i + 1}
                    </a>
                  ))}
                </div>
              )}

              {participation.durationMinutes && (
                <div className="challenges__participation-duration">
                  <Timer size={14} />
                  {participation.durationMinutes >= 60
                    ? `${Math.floor(participation.durationMinutes / 60)}h ${participation.durationMinutes % 60}m`
                    : `${participation.durationMinutes}m`}
                </div>
              )}
            </Card>
          ))}
        </div>
      ) : (
        <EmptyState
          title="No completed challenges yet"
          message="Complete your first challenge and your achievements will appear here."
          icon={<Trophy size={48} />}
          action={{
            label: 'Browse Library',
            onClick: () => setActiveTab('library'),
          }}
        />
      )}
    </div>
  );

  return (
    <AppLayout pageTitle="Challenges">
      <div className="challenges">
        {/* Ambient background */}
        <div className="challenges__ambient">
          <div className="challenges__ambient-shape challenges__ambient-shape--1" />
          <div className="challenges__ambient-shape challenges__ambient-shape--2" />
        </div>

        {/* Main Content */}
        <main className="challenges__main">
          {/* Intro */}
          <div className="challenges__intro">
            <p>Push your creative boundaries. Grow through challenges.</p>
          </div>

          {/* Tabs */}
          <div className="challenges__tabs">
            <button
              className={`challenges__tab ${activeTab === 'library' ? 'challenges__tab--active' : ''}`}
              onClick={() => setActiveTab('library')}
            >
              <Search size={16} />
              Library
            </button>
            <button
              className={`challenges__tab ${activeTab === 'active' ? 'challenges__tab--active' : ''}`}
              onClick={() => setActiveTab('active')}
            >
              <Flame size={16} />
              Active
              {activeChallenges.length > 0 && (
                <span className="challenges__tab-badge">{activeChallenges.length}</span>
              )}
            </button>
            <button
              className={`challenges__tab ${activeTab === 'completed' ? 'challenges__tab--active' : ''}`}
              onClick={() => setActiveTab('completed')}
            >
              <Trophy size={16} />
              Completed
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'library' && renderLibrary()}
          {activeTab === 'active' && renderActive()}
          {activeTab === 'completed' && renderCompleted()}
        </main>

        {/* FAB for creating custom challenge */}
        <div className="challenges__fab-container">
          <button
            className="challenges__fab"
            onClick={() => setShowCreateModal(true)}
            aria-label="Create custom challenge"
          >
            <Plus size={28} />
          </button>
        </div>

        {/* Challenge Detail Modal */}
        <Modal
          isOpen={!!selectedChallenge}
          onClose={() => { setSelectedChallenge(null); setChallengeResults([]); }}
          title={selectedChallenge?.title}
          size="lg"
        >
          {selectedChallenge && (
            <div className="challenges__detail">
              <div className="challenges__detail-badges">
                <div className="challenges__discipline-badges">
                  {selectedChallenge.disciplines?.map((d: string) => (
                    <span key={d} className="challenges__discipline-badge">
                      {disciplineOptions.find(o => o.value === d)?.label || d}
                    </span>
                  ))}
                </div>
                <span className={`challenges__difficulty challenges__difficulty--${difficultyMeta[selectedChallenge.difficulty]?.color || 'warning'}`}>
                  {difficultyMeta[selectedChallenge.difficulty]?.label || selectedChallenge.difficulty}
                </span>
                {selectedChallenge.isSystem && (
                  <span className="challenges__system-badge">System</span>
                )}
              </div>

              <p className="challenges__detail-description">{selectedChallenge.description}</p>

              <div className="challenges__detail-stats">
                <div className="challenges__detail-stat">
                  <Timer size={18} />
                  <div>
                    <span className="challenges__detail-stat-value">
                      {formatDuration(selectedChallenge.duration, selectedChallenge.durationType)}
                    </span>
                    <span className="challenges__detail-stat-label">Duration</span>
                  </div>
                </div>
                <div className="challenges__detail-stat">
                  <Users size={18} />
                  <div>
                    <span className="challenges__detail-stat-value">{selectedChallenge.participantCount}</span>
                    <span className="challenges__detail-stat-label">Participants</span>
                  </div>
                </div>
                <div className="challenges__detail-stat">
                  <CheckCircle2 size={18} />
                  <div>
                    <span className="challenges__detail-stat-value">{selectedChallenge.completionCount}</span>
                    <span className="challenges__detail-stat-label">Completions</span>
                  </div>
                </div>
              </div>

              <div className="challenges__detail-action">
                <Button
                  onClick={() => handleJoinChallenge(selectedChallenge.id)}
                  loading={joiningId === selectedChallenge.id}
                  fullWidth
                >
                  <Flag size={16} />
                  Join This Challenge
                </Button>
              </div>

              {/* Results from other participants */}
              <div className="challenges__detail-results">
                <h4 className="challenges__detail-results-title">Community Results</h4>
                {isLoadingResults ? (
                  <div className="challenges__loading challenges__loading--sm">
                    <Loader2 size={20} className="challenges__loading-spinner" />
                    <p>Loading results...</p>
                  </div>
                ) : challengeResults.length > 0 ? (
                  <div className="challenges__results-list">
                    {challengeResults.map((result) => (
                      <div key={result.id} className="challenges__result-item">
                        <div className="challenges__result-header">
                          <span className="challenges__result-status">
                            {result.status === 'Completed' ? (
                              <CheckCircle2 size={14} className="challenges__result-icon--completed" />
                            ) : (
                              <Clock size={14} />
                            )}
                            {result.status}
                          </span>
                          <span className="challenges__result-date">
                            {result.completedAt ? formatDate(result.completedAt) : formatDate(result.joinedAt)}
                          </span>
                        </div>
                        {result.resultText && (
                          <p className="challenges__result-text">{result.resultText}</p>
                        )}
                        {result.durationMinutes && (
                          <span className="challenges__result-duration">
                            <Timer size={12} />
                            {result.durationMinutes >= 60
                              ? `${Math.floor(result.durationMinutes / 60)}h ${result.durationMinutes % 60}m`
                              : `${result.durationMinutes}m`}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="challenges__detail-no-results">
                    No results yet. Be the first to complete this challenge!
                  </p>
                )}
              </div>
            </div>
          )}
        </Modal>

        {/* Create Challenge Modal */}
        <Modal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          title="Create Custom Challenge"
          size="md"
        >
          <div className="challenges__create-form">
            <Input
              placeholder="Challenge title"
              value={createForm.title}
              onChange={(e) => setCreateForm(prev => ({ ...prev, title: e.target.value }))}
              autoFocus
            />

            <Textarea
              placeholder="Describe the challenge..."
              value={createForm.description}
              onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
            />

            <div className="challenges__create-disciplines">
              <label className="challenges__create-label">Disciplines</label>
              <div className="challenges__discipline-checkboxes">
                {disciplineOptions.map(opt => (
                  <label key={opt.value} className="challenges__discipline-checkbox">
                    <input
                      type="checkbox"
                      checked={createForm.disciplines.includes(opt.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setCreateForm(prev => ({ ...prev, disciplines: [...prev.disciplines, opt.value] }));
                        } else {
                          setCreateForm(prev => ({ ...prev, disciplines: prev.disciplines.filter(d => d !== opt.value) }));
                        }
                      }}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <Select
              label="Difficulty"
              options={difficultyOptions.filter(o => o.value !== '')}
              value={createForm.difficulty}
              onChange={(e) => setCreateForm(prev => ({ ...prev, difficulty: e.target.value }))}
            />

            <div className="challenges__create-row">
              <Input
                type="number"
                placeholder="Duration"
                value={String(createForm.duration)}
                onChange={(e) => setCreateForm(prev => ({ ...prev, duration: Number(e.target.value) || 1 }))}
                min={1}
              />
              <Select
                options={durationTypeOptions}
                value={createForm.durationType}
                onChange={(e) => setCreateForm(prev => ({ ...prev, durationType: e.target.value }))}
              />
            </div>

            <div className="challenges__create-actions">
              <Button variant="ghost" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateChallenge}
                loading={isCreating}
                disabled={isCreating || !createForm.title.trim() || !createForm.description.trim() || createForm.disciplines.length === 0}
              >
                Create Challenge
              </Button>
            </div>
          </div>
        </Modal>

        {/* Complete Challenge Modal */}
        <Modal
          isOpen={showCompleteModal}
          onClose={() => setShowCompleteModal(false)}
          title="Complete Challenge"
          size="md"
        >
          <div className="challenges__complete-form">
            <Textarea
              placeholder="Share your results... (optional)"
              value={completeForm.resultText || ''}
              onChange={(e) => setCompleteForm(prev => ({ ...prev, resultText: e.target.value }))}
              rows={4}
            />

            <div className="challenges__complete-media">
              <label className="challenges__complete-label">Media URLs (optional)</label>
              <div className="challenges__complete-media-input">
                <Input
                  placeholder="Paste image or media URL..."
                  value={mediaUrlInput}
                  onChange={(e) => setMediaUrlInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddMediaUrl();
                    }
                  }}
                />
                <Button size="sm" variant="secondary" onClick={handleAddMediaUrl} disabled={!mediaUrlInput.trim()}>
                  Add
                </Button>
              </div>
              {completeForm.resultMediaUrls && completeForm.resultMediaUrls.length > 0 && (
                <div className="challenges__complete-media-list">
                  {completeForm.resultMediaUrls.map((url, i) => (
                    <div key={i} className="challenges__complete-media-item">
                      <ImageIcon size={14} />
                      <span>{url}</span>
                      <button onClick={() => handleRemoveMediaUrl(i)}>
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Input
              type="number"
              placeholder="Time spent in minutes (optional)"
              value={completeForm.durationMinutes ? String(completeForm.durationMinutes) : ''}
              onChange={(e) => setCompleteForm(prev => ({ ...prev, durationMinutes: Number(e.target.value) || undefined }))}
              min={1}
            />

            <div className="challenges__complete-actions">
              <Button variant="ghost" onClick={() => setShowCompleteModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCompleteChallenge}
                loading={isCompleting}
                disabled={isCompleting}
              >
                <Trophy size={16} />
                Mark Complete
              </Button>
            </div>
          </div>
        </Modal>

        {/* Abandon Confirm Modal */}
        <ConfirmModal
          isOpen={showAbandonConfirm}
          onClose={() => { setShowAbandonConfirm(false); setAbandoningChallengeId(null); }}
          onConfirm={handleAbandonChallenge}
          title="Abandon Challenge?"
          message="Are you sure you want to abandon this challenge? You can always join it again later."
          confirmText="Abandon"
          cancelText="Keep Going"
          variant="danger"
          isLoading={isAbandoning}
        />
      </div>
    </AppLayout>
  );
};

export default Challenges;
